/**
 * Backend API Service
 *
 * Servizio centralizzato per comunicazione con Laravel backend
 * Usa axios per chiamate HTTP con Bearer token per autenticazione
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8187/api';

// Crea istanza axios con configurazione base
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor: aggiunge Bearer token se disponibile
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: gestisce errori e 401 auto-logout
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Token scaduto o non valido → pulizia e redirect a login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
    }
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error.response?.data || error);
  }
);

// ==================== PRODUCTS ====================

export const getProducts = async (params = {}) => {
  return await apiClient.get('/products', { params });
};

export const getProductBySlug = async (slug) => {
  return await apiClient.get(`/products/${slug}`);
};

export const getProductsByCategory = async (category) => {
  return await apiClient.get(`/products/category/${category}`);
};

export const getCategories = async () => {
  return await apiClient.get('/products/categories');
};

// ==================== NEWS ====================

export const getNews = async (params = {}) => {
  return await apiClient.get('/news', { params });
};

export const getNewsBySlug = async (slug) => {
  return await apiClient.get(`/news/${slug}`);
};

// ==================== CART ====================

export const addToCart = async (sessionId, productId, quantity) => {
  return await apiClient.post('/cart/add', {
    session_id: sessionId,
    product_id: productId,
    quantity: quantity,
  });
};

export const getCart = async (sessionId) => {
  return await apiClient.get(`/cart/${sessionId}`);
};

// Carrello utente autenticato (dopo merge guest→user)
export const getUserCart = async () => {
  return await apiClient.get('/cart/user');
};

export const updateCartItem = async (itemId, quantity) => {
  return await apiClient.put(`/cart/${itemId}`, { quantity });
};

export const removeCartItem = async (itemId) => {
  return await apiClient.delete(`/cart/${itemId}`);
};

export const clearCart = async (sessionId) => {
  return await apiClient.delete(`/cart/clear/${sessionId}`);
};

// Svuota carrello utente autenticato (usa user_id dal token)
export const clearUserCart = async () => {
  return await apiClient.delete('/cart/clear-user');
};

// Merge carrello guest con utente loggato (solo session_id, user dal token)
export const mergeGuestCart = async (sessionId) => {
  return await apiClient.post('/cart/merge', {
    session_id: sessionId,
  });
};

// ==================== PAYPAL ====================

export const createPayPalOrder = async (orderData) => {
  return await apiClient.post('/paypal/create-order', orderData);
};

// session_id rimosso: il backend usa user_id dal token
export const capturePayPalOrder = async (orderId) => {
  return await apiClient.post('/paypal/capture-order', {
    order_id: orderId,
  });
};

export const getOrder = async (orderId) => {
  return await apiClient.get(`/orders/${orderId}`);
};

// ==================== AUTH ====================

export const register = async (userData) => {
  return await apiClient.post('/auth/register', userData);
};

export const login = async (email, password) => {
  return await apiClient.post('/auth/login', { email, password });
};

export const logout = async () => {
  return await apiClient.post('/auth/logout');
};

export const getAuthUser = async () => {
  return await apiClient.get('/auth/user');
};

export const updateUserProfile = async (userData) => {
  return await apiClient.put('/auth/user/update', userData);
};

export const changePassword = async (passwordData) => {
  return await apiClient.put('/auth/user/password', passwordData);
};

export const forgotPassword = async (email) => {
  return await apiClient.post('/auth/forgot-password', { email });
};

export const resetPassword = async (data) => {
  return await apiClient.post('/auth/reset-password', data);
};

// ==================== STRIPE ====================

export const createStripePaymentIntent = async (shippingData) => {
  return await apiClient.post('/stripe/create-payment-intent', shippingData);
};

export const confirmStripePayment = async (paymentIntentId, orderId) => {
  return await apiClient.post('/stripe/confirm-payment', {
    payment_intent_id: paymentIntentId,
    order_id: orderId,
  });
};

// ==================== ORDERS ====================

export const getUserOrders = async (params = {}) => {
  return await apiClient.get('/orders', { params });
};

export const getOrderById = async (orderId) => {
  return await apiClient.get(`/orders/${orderId}`);
};

export const trackOrder = async (orderId) => {
  return await apiClient.get(`/orders/${orderId}/track`);
};

export default {
  // News
  getNews,
  getNewsBySlug,
  // Products
  getProducts,
  getProductBySlug,
  getProductsByCategory,
  getCategories,
  // Cart
  addToCart,
  getCart,
  getUserCart,
  updateCartItem,
  removeCartItem,
  clearCart,
  mergeGuestCart,
  // PayPal
  createPayPalOrder,
  capturePayPalOrder,
  getOrder,
  // Auth
  register,
  login,
  logout,
  getAuthUser,
  updateUserProfile,
  changePassword,
  // Stripe
  createStripePaymentIntent,
  confirmStripePayment,
  // Orders
  getUserOrders,
  getOrderById,
  trackOrder,
};
